import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useProfile } from "@/hooks/use-profile";
import { useAuth } from "@/contexts/AuthContext";

interface CommentsInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: () => void;
  isLoading?: boolean;
  userAvatar?: string;
  userName?: string;
}

const CommentsInput = React.memo(({ 
  value, 
  onChange, 
  onSubmit,
  isLoading,
  userAvatar,
  userName
}: CommentsInputProps) => {
  const { user } = useAuth();
  const { profile } = useProfile(user?.id);

  return (
    <div className="flex gap-4 items-start mb-6">
      <Avatar className={cn(
        "h-8 w-8",
        profile?.is_admin && "ring-2 ring-[#F97316]",
        profile?.is_premium && "ring-2 ring-[#E5DEFF]",
        !profile?.is_admin && !profile?.is_premium && "ring-1 ring-primary/20"
      )}>
        <AvatarImage src={userAvatar} />
        <AvatarFallback>{userName?.charAt(0) || 'U'}</AvatarFallback>
      </Avatar>
      <div className="flex-1 space-y-2">
        <Textarea
          value={value}
          onChange={onChange}
          placeholder="Add a comment..."
          className="min-h-[80px] bg-white/5 border-white/10"
        />
        <div className="flex justify-end">
          <Button 
            variant="secondary" 
            size="sm"
            onClick={onSubmit}
            disabled={!value.trim() || isLoading}
          >
            {isLoading ? 'Posting...' : 'Post'}
          </Button>
        </div>
      </div>
    </div>
  );
});

CommentsInput.displayName = 'CommentsInput';

export default CommentsInput;
